import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle } from 'lucide-react';

/**
 * GenerationTimer Component
 * Displays elapsed time during AI report generation
 * Timer stops when generation completes but remains visible until explicitly hidden
 * 
 * @param {boolean} isGenerating - Whether generation is currently active
 * @param {boolean} isVisible - Whether to show/hide the timer component
 * @param {function} onReset - Callback when timer should be reset (optional)
 * @param {string} label - Label to display (optional)
 * @param {string} className - Additional CSS classes (optional)
 */
const GenerationTimer = ({ 
  isGenerating, 
  isVisible = true,
  onReset,
  label = "Generating", 
  className = "" 
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const wasGenerating = useRef(false);

  useEffect(() => {
    let interval = null;

    if (isGenerating) {
      // Start fresh when generation begins
      if (!wasGenerating.current) {
        setElapsedTime(0);
        setIsCompleted(false);
      }
      
      // Update timer every second while generating
      interval = setInterval(() => {
        setElapsedTime(prevTime => prevTime + 1);
      }, 1000);
      
      wasGenerating.current = true;
    } else {
      // Stop counting when generation completes
      if (interval) {
        clearInterval(interval);
      }
      
      // Mark as completed if it was generating before
      if (wasGenerating.current) {
        setIsCompleted(true);
      }
      
      wasGenerating.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isGenerating]);

  // Reset timer when visibility is toggled off
  useEffect(() => {
    if (!isVisible) {
      setElapsedTime(0);
      setIsCompleted(false);
      wasGenerating.current = false;
      if (onReset) {
        onReset();
      }
    }
  }, [isVisible, onReset]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Different styling for completed state
  const bgColor = isCompleted ? 'bg-green-50' : 'bg-blue-50';
  const textColor = isCompleted ? 'text-green-700' : 'text-blue-700';
  const icon = isCompleted ? (
    <CheckCircle className="w-4 h-4" />
  ) : (
    <Clock className="w-4 h-4 animate-pulse" />
  );
  const displayLabel = isCompleted ? 'Completed in' : label;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${bgColor} ${textColor} rounded-full text-sm font-medium ${className}`}>
      {icon}
      <span>{displayLabel}</span>
      <span className="font-mono font-bold">{formatTime(elapsedTime)}</span>
    </div>
  );
};

export default GenerationTimer;
